/**
 * DashboardLayout: sidebar + top bar + the page's scroll area.
 *
 * Structure (h-dvh, nothing sized with calc):
 *   [sidebar: docked from lg, a drawer below lg] [column: header h-16, h-14 on short screens, matching the sidebar logo row | main (the only page scroller)]
 * The header lives in the content column, so it always starts where the sidebar ends (collapsed or not).
 * `main` is `flex-1 min-h-0 overflow-auto` with the page padding; a page that wants to fill the height (tool
 * pages, via WorkspaceLayout) uses h-full inside it.
 *
 * Top bar: a breadcrumb (page icon + name, with the parent for detail pages such as Batch) on the left and only
 * Docs and the theme toggle on the right; Feedback and What's new live in the sidebar's Help menu. One hairline
 * bottom border, same white surface as the content (the sidebar sits a step off it on bg-canvas).
 *
 * Below lg the sidebar is an off-canvas drawer: it is inert while closed (no hidden tab stops), opens from the
 * labelled menu button, closes on Escape, on the backdrop and on navigation, and returns focus to the menu button.
 *
 * `variant="admin"` is the admin shell (layout/AdminLayout.tsx): the same structure with the admin sidebar and crumbs,
 * only for users with role admin (everyone else goes back to /dashboard), and without the user-side prompts.
 */
import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { Geist } from "next/font/google";
import { Icon } from "@iconify/react";

import SideBar from "@/components/layout/SideBar";
import AcquisitionSourceModal from "@/components/dashboard/AcquisitionSourceModal";
import { WhatsNewAnnouncer } from "@/components/layout/WhatsNew";
import ThemeToggle from "@/components/ui/ThemeToggle";
import IconButton from "@/components/ui/IconButton";
import { getCrumbs } from "@/components/layout/navConfig";

import { useProtectedRoute } from "@/hooks/useProtectedRoute";
import { useEscape, useIsDesktop, useLayer } from "@/hooks/useOverlay";

interface LayoutProps {
  children: ReactNode;
  variant?: "app" | "admin";
}

const geistSans = Geist({
  variable: "--font-geist-sans",
  subsets: ["latin"],
});

const headerLink =
  "inline-flex h-10 items-center gap-1.5 rounded-control px-2.5 text-sm font-medium text-fg-secondary transition-colors hover:bg-black/5 hover:text-fg dark:hover:bg-white/5 cursor-pointer sm:h-9";

function Breadcrumb({ pathname }: { pathname: string }) {
  const crumbs = getCrumbs(pathname);
  return (
    <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
      <ol className="flex min-w-0 items-center gap-1.5 text-sm">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <Fragment key={c.label}>
              {i > 0 && (
                <li aria-hidden="true" className="hidden shrink-0 text-fg-muted sm:block">
                  <Icon icon="lucide:chevron-right" className="text-sm" />
                </li>
              )}
              <li className={`flex min-w-0 items-center ${last ? "" : "hidden shrink-0 sm:flex"}`}>
                {c.href && !last ? (
                  <Link
                    href={c.href}
                    className="flex items-center gap-2 rounded-sm text-fg-muted transition-colors hover:text-fg"
                  >
                    <Icon icon={c.icon} className="shrink-0 text-base" aria-hidden="true" />
                    {c.label}
                  </Link>
                ) : (
                  <span aria-current={last ? "page" : undefined} className="flex min-w-0 items-center gap-2 font-medium text-fg">
                    <Icon
                      icon={c.icon}
                      className={`shrink-0 text-base text-fg-muted ${i > 0 ? "sm:hidden" : ""}`}
                      aria-hidden="true"
                    />
                    <span className="truncate">{c.label}</span>
                  </span>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

export default function Layout({ children, variant = "app" }: LayoutProps) {
  const { user, loading } = useProtectedRoute();
  const router = useRouter();
  const isAdmin = variant === "admin";
  const allowed = !!user && (!isAdmin || user.role === "admin");
  useEffect(() => {
    if (isAdmin && !loading && user && user.role !== "admin") router.replace("/dashboard");
  }, [isAdmin, loading, user, router]);
  const isDesktop = useIsDesktop();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  // Mounted at the layout level (not a single page) since a user could land
  // on any dashboard sub-route first. Gating is server-side truth
  // (acquisitionPromptPending, from /auth/me), not localStorage.
  const [showAcquisitionModal, setShowAcquisitionModal] = useState(false);
  useEffect(() => {
    if (!isAdmin && !loading && user?.acquisitionPromptPending) setShowAcquisitionModal(true);
  }, [isAdmin, loading, user?.acquisitionPromptPending]);

  const drawerOpen = isSidebarOpen && !isDesktop;
  const closeDrawer = useCallback(() => {
    setIsSidebarOpen(false);
    requestAnimationFrame(() => menuButtonRef.current?.focus({ preventScroll: true }));
  }, []);
  const isTop = useLayer(drawerOpen);
  useEscape(drawerOpen, closeDrawer, isTop);

  // Move focus into the drawer when it opens.
  useEffect(() => {
    if (!drawerOpen) return;
    const raf = requestAnimationFrame(() => {
      drawerRef.current?.querySelector<HTMLElement>("[data-drawer-close]")?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [drawerOpen]);

  // Close the drawer on navigation and when the screen grows to desktop.
  useEffect(() => {
    const done = () => setIsSidebarOpen(false);
    router.events.on("routeChangeStart", done);
    return () => router.events.off("routeChangeStart", done);
  }, [router.events]);
  useEffect(() => {
    if (isDesktop) setIsSidebarOpen(false);
  }, [isDesktop]);

  if (loading || !allowed) {
    return (
      <div className="flex h-dvh w-full items-center justify-center bg-canvas" role="status" aria-live="polite">
        <p className="text-sm text-fg-muted">{isAdmin ? "Loading admin..." : "Loading your dashboard..."}</p>
      </div>
    );
  }

  return (
    <div className={`${geistSans.className} flex h-dvh overflow-hidden text-gray-900 dark:text-gray-100`}>
      <a
        href="#main-content"
        className="sr-only z-[10060] rounded-control bg-surface px-3 py-2 text-sm font-medium text-fg shadow-popover focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
      >
        Skip to content
      </a>

      {/* Drawer backdrop (below lg) */}
      {drawerOpen && <div className="fixed inset-0 z-20 bg-black/40 lg:hidden" onClick={closeDrawer} aria-hidden="true" />}

      {/* Sidebar: docked at lg, off-canvas drawer below */}
      <div
        id="app-sidebar"
        ref={drawerRef}
        inert={!isDesktop && !isSidebarOpen}
        {...(drawerOpen ? { role: "dialog", "aria-modal": true, "aria-label": "Navigation" } : {})}
        className={`fixed inset-y-0 left-0 z-30 h-full transform transition-transform duration-200 ease-out lg:static lg:z-auto lg:translate-x-0 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SideBar onClose={closeDrawer} isDrawer={!isDesktop} variant={variant} />
      </div>

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col bg-surface">
        <header className="flex h-16 shrink-0 [@media(max-height:860px)]:h-14 items-center gap-2 border-b border-line bg-surface px-2 sm:px-4 lg:px-8">
          <IconButton
            ref={menuButtonRef}
            icon="lucide:menu"
            label="Open navigation"
            aria-expanded={isSidebarOpen}
            aria-controls="app-sidebar"
            className="lg:hidden"
            size="md"
            onClick={() => setIsSidebarOpen(true)}
          />

          <Breadcrumb pathname={router.pathname} />

          <div className="flex shrink-0 items-center gap-1">
            <a
              href="https://docs.spidra.io/"
              target="_blank"
              rel="noopener noreferrer"
              className={headerLink}
              aria-label="Docs (opens in a new tab)"
            >
              <Icon icon="lucide:book-open" className="text-base" aria-hidden="true" />
              <span className="hidden sm:inline">Docs</span>
            </a>
            <ThemeToggle variant="header" />
          </div>
        </header>

        <main id="main-content" tabIndex={-1} className="min-h-0 flex-1 overflow-auto px-4 py-6 outline-none lg:px-8">
          {children}
        </main>
      </div>

      {/* One-time "how did you hear about Spidra?" prompt */}
      {!isAdmin && <AcquisitionSourceModal isOpen={showAcquisitionModal} onClose={() => setShowAcquisitionModal(false)} />}
      {!isAdmin && <WhatsNewAnnouncer />}
    </div>
  );
}
